import { useQuery } from "@tanstack/react-query";
import { fetchBillingStatus } from "../api/billing";
import { useAuth } from "./useAuth";

// ─── useHistoryGate ───────────────────────────────────────────────────────────
// Everything a surface needs to explain the free-plan history window: is the
// account gated, the exact cutoff date, whether an import has actually
// skipped older activities (and how far back), and whether the dismissible
// notice was dismissed. Shares the ["billing"] cache with useIsPro/PlanPage.
// `gated` is false until billing has answered, so nothing flashes for Pro
// users on first paint.

export interface HistoryGate {
  /** True once billing has answered and the account has a history cutoff. */
  gated: boolean;
  /** The cutoff date, or null on Pro / while loading. */
  cutoff: Date | null;
  /** "24 September 2025" - for copy. Empty while loading. */
  cutoffLabel: string;
  /** Free-plan window in months (12). */
  months: number;
  /** An import path has actually skipped activities older than the cutoff. */
  truncated: boolean;
  /** Oldest skipped start date, when known. */
  oldestSkippedAt: Date | null;
  /** The dashboard notice was dismissed (server-side). */
  dismissed: boolean;
  /** Full history is unlocked (spec 30): no cutoff, older heatmap years open. */
  fullHistory: boolean;
  isLoading: boolean;
}

export function formatCutoff(date: Date): string {
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function useHistoryGate(): HistoryGate {
  const { user, isLoading: authLoading } = useAuth();
  const billingQuery = useQuery({
    queryKey: ["billing"],
    queryFn: fetchBillingStatus,
    staleTime: 60 * 1000,
    enabled: user != null,
  });
  const billing = billingQuery.data;
  const cutoff = parseDate(billing?.historyCutoff);
  return {
    // Spec 30: the cutoff, not Pro, decides (monthly Pro is still gated
    // until Full history is unlocked).
    gated: billing != null && cutoff != null,
    cutoff,
    cutoffLabel: cutoff ? formatCutoff(cutoff) : "",
    months: billing?.limits.historyMonths ?? 12,
    truncated: billing?.historyTruncated ?? false,
    oldestSkippedAt: parseDate(billing?.historyOldestSkippedAt),
    dismissed: billing?.historyNoticeDismissedAt != null,
    fullHistory: billing?.fullHistory.unlocked ?? false,
    isLoading: authLoading || (user != null && billingQuery.isLoading),
  };
}
